/**
 * 前端埋点SDK
 * 用于收集用户行为数据、页面访问数据等
 */

/**
 * 埋点事件类型
 */
export enum TrackingEventType {
  /**
   * 页面浏览
   */
  'PageView' = 'pageView',
  /**
   * 页面停留
   */
  'PageStay' = 'pageStay',
  /**
   * 点击事件
   */
  'Click' = 'click',
  /**
   * 查询
   */
  'Search' = 'search',
  /**
   * js代码错误上报
   */
  'JsError' = 'jsError',
  /**
   * 接口请求错误上报
   */
  'ApiError' = 'apiError',
  /**
   * 曝光埋点
   */
  'Exposure' = 'exposure',
  /**
   * 自定义事件
   */
  'CustomEvent' = 'custom',
  /**
   * 支付成功
   */
  'PaySuccess' = 'paySuccess',
  /**
   * 支付失败
   */
  'PayFail' = 'payFail',
}

/**
 * 埋点数据类型定义
 */
export interface TrackingEventData {
  /**
   * 随机id
   */
  ramdomId: string;
  /**
   * 事件类型
   */
  eventType: TrackingEventType;
  /**
   * 事件名称
   */
  eventName: string;
  /**
   * 页面路径
   */
  pagePath?: string;
  /**
   * 页面标题
   */
  pageTitle?: string;
  /**
   * 额外参数
   */
  params?: string | null;
  /**
   * 查询功能的字典
   */
  dictKey?: string;
  /**
   * 查询功能的字典名
   */
  productName?: string;
  /**
   * 时间戳
   */
  timestamp: number;
  /**
   * 用户ID
   */
  userId?: string;
  /**
   * 浏览器指纹
   */
  fingerprint?: string | number;
  /**
   * 设备类型
   */
  userAgent?: string;
  /**
   * 屏幕分辨率
   */
  screenResolution?: string;
  /**
   * 页面停留时间(毫秒)，仅在TrackingEventType为page_stay时有效
   */
  stayDuration?: number;
  /**
   * 异常信息，仅在TrackingEventType为js_error或api_error时有效
   */
  sysReportErrorInfoDto?: {
    /**
     * 错误消息
     */
    message: string;
    /**
     * 错误堆栈(js_error)或响应数据(api_error)
     */
    stack?: string;
    /**
     * 错误类型
     */
    type?: string;
    /**
     * 请求URL(api_error)
     */
    url?: string;
    /**
     * 请求方法(api_error)
     */
    method?: string;
    /**
     * 响应状态码(api_error)
     */
    status?: number;
    /**
     * 请求参数(api_error)
     */
    params?: any;
    /**
     * 环境
     */
    env?: string;
  } | null;
}

/**
 * 埋点配置
 */
export interface TrackingConfig {
  /**
   * 是否启用埋点
   */
  enabled?: boolean;
  /**
   * 上报地址
   */
  endpoint?: string;
  /**
   * 是否自动上报页面浏览
   */
  autoPageView?: boolean;
  /**
   * 是否自动上报点击事件
   */
  autoClickTracking?: boolean;
  /**
   * 是否记录设备信息
   */
  recordDeviceInfo?: boolean;
  /**
   * 是否在开发环境打印日志
   */
  debug?: boolean;
  /**
   * 路由设置
   */
  routerList?: Routes[];
  /**
   * 用户id
   */
  userId?: string;
  /**
   * sourceMap 启用
   */
  sourceMapEnable?: boolean;
}

/**
 * 事件触发类型
 */
export interface TrackType extends Pick<
  TrackingEventData,
  | 'eventName'
  | 'params'
  | 'sysReportErrorInfoDto'
  | 'dictKey'
  | 'productName'
  | 'stayDuration'
  | 'pagePath'
  | 'pageTitle'
> {
  eventType: TrackingEventData['eventType'];
  previousPagePath?: string;
}

/**
 * 权限
 */
export interface Routes {
  /**
   * 子菜单
   */
  children?: Routes[];
  /**
   * 唯一id
   */
  id?: string;
  /**
   * 资源名称;
   */
  name?: string;
  /**
   * 父级id
   */
  parentId?: number;
  /**
   * 页面路由;
   */
  path: string;
  /**
   * 资源名称;
   */
  meta?: {
    title: string;
  };
}

export interface StackTraceType {
  fileName: string;
  lineNumber: number;
  columnNumber: number;
  functionName: string;
}

declare module 'source-map-js' {
  interface SourceMapConsumer {
    destroy: () => void;
  }
}

export interface ErrorTrackResDto {
  message: string;
  stack: string;
  type: string;
  env?: string;
}
